import { useEffect, useMemo, useRef, useState } from 'react'

import { Flex, Heading, Notice, Tabs } from '@applyboard/crystal-ui'
import { BulletFillIcon } from '@applyboard/ui-icons'
import styled from '@emotion/styled'
import { DeferralRequest, DocumentType } from '@applyboard/school-applications-types-lib'
import { find } from 'lodash'
import { Navigate, useParams } from 'react-router-dom'
import {
  ApplicationForms,
  ApplicationRequests,
  ApplicationTimeline,
  BackToApplication,
} from '../../components/Application'
import { DesktopHeader } from '../../components/Application/ApplicationHeader/DesktopHeader'
import ApplicationStateTag from '../../components/Application/ApplicationStateTag/ApplicationStateTag'
import { Loading } from '../../components/Loading'
import { NavBars } from '../../components/NavBars'
import { ApplicationState, useGetApplication } from '../../hooks'
import { ApplicationSteps, hasApplicationFee, up } from '../../utils'
import { TopMenu } from './TopMenu'
import { ApplicationDetails } from '../../components/Application/ApplicationDetails'
import { DeferralRequestNotice } from './DeferralRequestNotice'
import { StudentApplication } from '../../components/Application/types'
import { useGetTimelineItems } from '../../hooks/useGetTimelineItems'
import { useGetApplicationPageProps } from '../../hooks/useGetApplicationPageProps'
import { useQueryClient } from '@tanstack/react-query'

enum TabLabel {
  Submitted = 1,
  Details,
  Requests,
}

export function ViewApplicationPage() {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const { isLoadingApplication, application } = useGetApplication({ id })
  const { isLoadingApplicationPageProps } = useGetApplicationPageProps()
  const [tab, setTab] = useState(`${TabLabel.Submitted}`)
  const [applicationHasDecisions, setApplicationHasDecisions] = useState(false)
  const [hasDocumentsRequested, setHasDocumentsRequested] = useState(false)
  const [currentStep, setCurrentStep] = useState(ApplicationSteps.PERSONAL_INFORMATION)
  const { lastTimeline } = useGetTimelineItems(application)
  const selectedTab = currentStep

  const viewApplicationPageRef = useRef<HTMLDivElement>(null)

  const applicationState = application?.attributes?.applicationState as ApplicationState

  const { isApplicationSubmitted, isDeferralRequested } = useMemo(() => {
    const isSubmitted =
      applicationState === ApplicationState.READY_FOR_SCREENING ||
      applicationState === ApplicationState.READY_FOR_ASSESSMENT

    const hasDeferralRequested =
      applicationState === ApplicationState.DEFERRAL_REQUESTED_IN_ASSESSMENT ||
      applicationState === ApplicationState.DEFERRAL_REQUESTED_IN_INITIAL_OFFER ||
      applicationState === ApplicationState.DEFERRAL_REQUESTED_IN_SCREENING

    return {
      isApplicationSubmitted: isSubmitted,
      isDeferralRequested: hasDeferralRequested,
    }
  }, [applicationState])

  useEffect(() => {
    setHasDocumentsRequested(application?.attributes?.hasOpenDocumentRequests ?? false)
  })

  useEffect(() => {
    if (currentStep > 0) {
      window.scrollTo(0, 0)
    }
  }, [currentStep])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoadingApplication && viewApplicationPageRef.current) {
        viewApplicationPageRef.current.focus()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [isLoadingApplication])

  useEffect(() => {
    let hasDecisions = false

    if (application?.attributes?.files) {
      hasDecisions = Object.values(application?.attributes?.files).some(
        item =>
          item?.type === DocumentType.PRE_OFFER_LETTER ||
          item?.type === DocumentType.DECLINED_LETTER ||
          item?.type === DocumentType.FINAL_OFFER_LETTER ||
          item?.type === DocumentType.WAITLIST_LETTER,
      )
    }

    setApplicationHasDecisions(hasDecisions)
  }, [application])

  useEffect(() => {
    if (applicationHasDecisions) {
      if (isDeferralRequested) {
        setTab(`${TabLabel.Submitted}`)
      } else {
        setTab(`${TabLabel.Details}`)
      }
    }
  }, [applicationHasDecisions, isDeferralRequested])

  const tabChange = (tab: number) => {
    queryClient.invalidateQueries({
      queryKey: ['applications', application?.id],
    })
    setCurrentStep(tab)
    window.scroll({
      top: 0,
    })
  }

  const onPreviousTabButton = () => {
    tabChange(selectedTab - 1)
  }

  const onNextTabButton = () => {
    tabChange(selectedTab + 1)
  }

  if (isLoadingApplication || isLoadingApplicationPageProps) {
    return (
      <NavBars>
        <Loading />
      </NavBars>
    )
  }

  if (!application) {
    return <Navigate to="/not-found" />
  }

  if (application.attributes?.applicationState === ApplicationState.DRAFT) {
    return <Navigate to={`/applications/new?applicationId=${application.id}`} />
  }

  return (
    <ApplicationWrapper tabIndex={-1} ref={viewApplicationPageRef}>
      <NavBars largePage>
        <BackToApplication
          label={`${application.attributes?.programSelected?.program?.name} Application`}
        />
        <Flex direction="column" gap={6}>
          <Flex wrap={false} align="center" justify="between" gap={4}>
            <Flex
              hideBelow={selectedTab > lastTimeline ? 'sm' : undefined}
              align={{ xs: 'start', md: 'center' }}
              direction={{ xs: 'column', md: 'row' }}
              gap={4}
              py={4}
            >
              <Heading level={1} variant="headlineL">
                Application for {application.attributes?.programSelected?.program?.name}
              </Heading>
              <ApplicationStateTag
                state={application.attributes?.applicationState as ApplicationState}
                hasOpenDocumentRequests={application.attributes?.hasOpenDocumentRequests}
              />
            </Flex>
            <TopMenu applicationId={application.id} />
          </Flex>
          {hasDocumentsRequested ? (
            <Flex grow={1}>
              <Notice intent="warning">
                Additional documents have been requested to complete the assessment of this
                application.
              </Notice>
            </Flex>
          ) : null}

          {application.attributes?.applicationState === ApplicationState.PAYMENT_PROCESSING ? (
            <Flex grow={1}>
              <Notice intent="primary">
                Your payment has been initiated and is currently being processed. Your application has
                not yet been submitted. If the payment does not complete successfully, you’ll be able
                to try again later using the same or a different payment method.
              </Notice>
            </Flex>
          ) : null}

          {application.attributes?.applicationState === ApplicationState.INITIAL_OFFER ? (
            <Notice intent="positive">Congratulations! You have received a pre-offer</Notice>
          ) : null}

          {application.attributes?.applicationState === ApplicationState.FINAL_OFFER_ISSUED ? (
            <Notice intent="positive">Congratulations! You have received a final offer</Notice>
          ) : null}

          {application.attributes?.applicationState === ApplicationState.WAITLISTED ? (
            <Notice intent="positive">Congratulations! You have been waitlisted</Notice>
          ) : null}

          {application.attributes?.applicationState === ApplicationState.DECLINED ? (
            <Notice intent="negative">
              After careful consideration, we regret to inform you that this application has not
              been accepted.
            </Notice>
          ) : null}

          {application.attributes?.deferralRequests ? (
            <DeferralRequestNotice
              deferralRequests={application.attributes.deferralRequests as DeferralRequest}
            />
          ) : null}

          {isApplicationSubmitted &&
          hasApplicationFee(application) &&
          !application.attributes?.deferralRequests ? (
            <Notice intent="positive">
              Your payment was successfully processed and your application is now submitted. You
              will be notified when a decision is made.
            </Notice>
          ) : null}
          <Flex gap={{ xs: 6, md: 12 }} direction={{ xs: 'column', md: 'row' }}>
            <Flex direction="column" gap={4} basis={{ xs: '100%', md: 278 }}>
              <DesktopHeader application={application} showProgramName={false} />
              {tab === `${TabLabel.Submitted}` ? (
                <Flex hideBelow="md">
                  <ApplicationTimeline
                    selectedTab={selectedTab}
                    setSelectedTab={tab => setCurrentStep(tab)}
                    application={application}
                  />
                </Flex>
              ) : null}
            </Flex>
            <TabWrapper>
              <Tabs
                onChange={value => setTab(`${value}`)}
                value={`${tab}`}
                aria-label="Application Tabs"
                orientation="horizontal"
              >
                <Tabs.Item value={`${TabLabel.Submitted}`} label={TabLabel[TabLabel.Submitted]}>
                  <Flex hideAbove="md" align="center" justify="center">
                    <ApplicationTimeline
                      selectedTab={selectedTab}
                      setSelectedTab={tab => setCurrentStep(tab)}
                      application={application}
                    />
                  </Flex>
                  <ApplicationForms
                    disabled
                    selectedTab={selectedTab}
                    application={application}
                    onPreviousTabButton={onPreviousTabButton}
                    onNextTabButton={onNextTabButton}
                    onSuccess={() => {
                      if (selectedTab <= lastTimeline) {
                        setCurrentStep(currentStep + 1)
                      }
                    }}
                  />
                </Tabs.Item>
                {applicationHasDecisions && !isDeferralRequested ? (
                  <Tabs.Item value={`${TabLabel.Details}`} label={TabLabel[TabLabel.Details]}>
                    <ApplicationDetails
                      programName={application.attributes?.programSelected?.program?.name}
                      campusName={application.attributes?.programSelected?.campus?.name}
                      intakeName={application.attributes?.programSelected?.programIntakeTerm?.name}
                      application={application as StudentApplication}
                      onOfferConditionsClick={() => setTab(`${TabLabel.Requests}`)}
                      applicationHasDecisions={applicationHasDecisions}
                    />
                  </Tabs.Item>
                ) : null}
                <Tabs.Item
                  value={`${TabLabel.Requests}`}
                  label={TabLabel[TabLabel.Requests]}
                  icon={
                    find(
                      application.attributes?.requestedDocuments,
                      r => r?.requestStatus === 'PENDING',
                    )
                      ? BulletFillIcon
                      : undefined
                  }
                >
                  <ApplicationRequests application={application} />
                </Tabs.Item>
              </Tabs>
            </TabWrapper>
          </Flex>
        </Flex>
      </NavBars>
    </ApplicationWrapper>
  )
}

const TabWrapper = styled.div({
  width: '100%',
  [up('md')]: {
    width: 'calc(100% - 326px)',
  },
  '[role="tablist"] path': {
    fill: '#d32252',
  },
})

const ApplicationWrapper = styled.div({
  outline: 'none',
})
